import type { StoreAuditEntry } from '@/client/types/stores';

/** ff-api audit `action` values (admin/view/store/*.py) → readable labels. */
const ACTION_LABEL: Record<string, string> = {
  'store.suspend': 'Suspended the store',
  'store.suspend.lift': 'Lifted the suspension',
  'store.verify': 'Verified the store',
  'store.verify.remove': 'Removed verification',
  'store.feature': 'Featured the store',
  'store.feature.remove': 'Unfeatured the store',
  'store.publish': 'Published the store',
  'store.publish.remove': 'Unpublished the store',
  'store.review.hold': 'Placed a review hold',
  'store.review.clear': 'Cleared the review hold',
  'store.close': 'Force-closed the store',
  'store.open': 'Re-opened the store',
  'store.ban': 'Banned the store',
  'store.ban.lift': 'Lifted the ban',
  'store.delete': 'Deleted the store',
  'store.transfer': 'Transferred ownership',
  'store.slug.set': 'Set the storefront handle',
  'store.slug.unlock': 'Unlocked the handle',
  'store.profile.update': 'Edited the profile',
  'store.category.update': 'Changed categories',
  'store.plan.update': 'Overrode the plan',
  'store.plan_exempt.set': 'Granted plan exemption',
  'store.plan_exempt.clear': 'Revoked plan exemption',
  'store.commission.set': 'Set a commission override',
  'store.commission.clear': 'Cleared the commission override',
  'store.payout.hold': 'Held payouts',
  'store.payout.release': 'Released payouts',
  'store.payout.config': 'Changed payout settings',
  'store.settings.update': 'Changed settings',
  'store.attributes.update': 'Changed store details',
  'store.media.update': 'Changed logo or banner',
  'store.venue.update': 'Changed the venue',
  'store.delivery.update': 'Changed delivery coverage',
  'store.listing.moderate': 'Moderated a listing',
  'store.listing.attach': 'Added a listing',
  'store.listing.detach': 'Removed a listing',
  'store.order.status': 'Updated an order',
  'store.order.cancel': 'Cancelled an order',
  'store.refund.approve': 'Approved a refund',
  'store.refund.reject': 'Declined a refund',
  'store.return.approve': 'Approved a return',
  'store.return.reject': 'Declined a return',
  'store.return.received': 'Marked a return received',
  'store.inventory.adjust': 'Adjusted stock',
  'store.pricing.bulk_update': 'Updated prices',
  'store.permissions.update': 'Changed staff permissions',
  'store.permissions.remove': 'Cleared a staff override',
  'store.permissions.preset': 'Applied a staff preset',
  'store.plan.renewal.cancel': 'Cancelled the plan renewal',
  'store.plan.renewal.resume': 'Resumed the plan renewal',
  'store.voucher.create': 'Created a voucher',
  'store.voucher.revoke': 'Revoked a voucher',
};

/** Actions that change the store's standing — shown on the Moderation tab. */
export const MODERATION_ACTIONS = new Set([
  'store.suspend', 'store.suspend.lift', 'store.verify', 'store.verify.remove',
  'store.feature', 'store.feature.remove', 'store.publish', 'store.publish.remove',
  'store.review.hold', 'store.review.clear', 'store.close', 'store.open',
  'store.ban', 'store.ban.lift', 'store.delete', 'store.transfer',
]);

export const auditLabel = (e: StoreAuditEntry): string =>
  (e.action && (ACTION_LABEL[e.action] ?? e.action.replace(/^store\./, '').replace(/[._]/g, ' '))) || 'Admin action';

export const auditWhen = (e: StoreAuditEntry): Date | null => {
  const raw = e.date ?? e.date_added ?? e.timestamp;
  if (raw == null) return null;
  const d = new Date(typeof raw === 'number' ? (raw < 1e12 ? raw * 1000 : raw) : String(raw));
  return Number.isNaN(d.getTime()) ? null : d;
};

export const auditActor = (e: StoreAuditEntry): string =>
  e.actor_name || (e.actor_uid != null ? `uid ${e.actor_uid}` : 'admin');

export const auditReason = (e: StoreAuditEntry): string | null =>
  e.reason ?? (e.meta?.reason as string | undefined) ?? null;

/** "3h ago", "2d ago", or a date for anything older than a week. */
export const relativeTime = (d: Date | null): string => {
  if (!d) return '';
  const mins = Math.round((Date.now() - d.getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};
